import React, { useState, useMemo } from "react";
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { Box, Typography, Button, Drawer, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalculateIcon from "@mui/icons-material/Calculate";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { AdjustedProductType, ContributionType, Product } from "../../interfaces/DTO/mislaka_xml_models";
import { MimshakService } from "../../services/mimshakService";
import StyledDataGrid from "../../shared/StyledDataGrid";
import { adjustedProductTypeColumn, percentageColumn, currencyColumn, handlerCodeColumn } from "../../shared/UIParts/columnsFormatter";
import { formatCurrency, formatIsoDateYearly, formatPercentageRound } from "../../utils/namesFormater";
import InsuranceProductDrawer from "../PersonFinancialData/Insurance/InsuranceProductDrawer";
import { LoanEligibilityConfigService } from "../../services/LoanEligibilityConfigService";
import LoanCalculator from "../Loan/LoanCalculator";
import { LoanChip } from "../PersonFinancialData/Chips/LoanChip";
import ExcelStyleFilter from "../../shared/UIParts/ExcelStyleFilter";
import { formatAdjustedProductType } from "../../utils/enumFormatter";
import { formatHandlerCode } from "../../utils/namesFormater";


interface LoanEligibilityTableProps {
  products: Product[];
  fundData?: FundDataBasic[];
}

// מוצרים בהם לא ניתן לקחת הלוואה על רכיב הפיצויים - יש להפחית אותו מהסכום הזכאי להלוואה
const COMPENSATION_EXCLUDED_ADJUSTED_PRODUCT_TYPES = new Set<AdjustedProductType>([
  AdjustedProductType.LIFE_INSURANCE_SAVINGS, // ביטוח מנהלים
  AdjustedProductType.ACTIVE_MANAGERS_INSURANCE, // ביטוח מנהלים
  AdjustedProductType.INACTIVE_MANAGERS_INSURANCE, // ביטוח מנהלים
  AdjustedProductType.MANDATORY_PENSION_FUND, // פנסיה מקיפה
  AdjustedProductType.SUPPLEMENTARY_PENSION_FUND, // פנסיה משלימה
  AdjustedProductType.PENSION_PROVIDENT_FUND, // קופת גמל פנסיונית
  AdjustedProductType.COMBINED_PROVIDENT_FUND, // קופת גמל הונית ופנסיונית
]);


const LoanEligibilityTable: React.FC<LoanEligibilityTableProps> = ({ products, fundData }) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isLoanCalculatorOpen, setIsLoanCalculatorOpen] = useState(false);
  const [hasOpenedLoanCalculator, setHasOpenedLoanCalculator] = useState(false);
  const [selectedProductTypes, setSelectedProductTypes] = useState<Set<string> | null>(null);
  const [selectedHandlerCodes, setSelectedHandlerCodes] = useState<Set<string> | null>(null);
  const [selectedLiquidityOptions, setSelectedLiquidityOptions] = useState<Set<string> | null>(null);

 const allRows = products.flatMap((product, productIndex) =>
  product.policies_or_accounts.flatMap((policy, policyIndex) => {
    const mergedPaths = MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths);
    // תאריך הנזילות מושווה למועד הדיווח של הפוליסה (update_date) ולא לתאריך הנוכחי בפועל,
    // כי הנתונים משקפים תמונת מצב נכון לאותו מועד
    const referencePeriod = policy.update_date;
    const futureSummaries = MimshakService.getFutureLiquiditySummaries([policy], referencePeriod);
    const liquidityDate = futureSummaries.length > 0 ? futureSummaries[0].liquidity_date : null;
    // יותר מתאריך נזילות עתידי אחד מובחן - לא ניתן להציג תאריך יחיד, הסכום הלא-נזיל יוצג כ"לא נזיל" סתם
    const distinctFutureDatesCount = new Set(futureSummaries.map((s) => s.liquidity_date)).size;
    // סכום שכבר נזיל בפועל (תאריך נזילות שעבר את מועד הדיווח). סכום עם תאריך נזילות עתידי, או ללא תאריך נזילות כלל, עדיין לא נזיל
    const alreadyLiquiedAmount = MimshakService.getPastLiquidityAmountSum([policy], referencePeriod);
    const isLiquid = alreadyLiquiedAmount > 0;

    // קבלת הלוואות עבור הפוליסה
    const loans = MimshakService.getAllLoans([policy]);
    const totalLoanAmount = MimshakService.calculateTotalLoanAmount([policy]);

    // עבור מוצרי פנסיה/ביטוח מנהלים/קופת גמל פנסיונית - לא ניתן לקחת הלוואה על רכיב הפיצויים
    const adjustedType = product.details.adjusted_product_type ?? product.details.product_type;
    const excludesCompensation = COMPENSATION_EXCLUDED_ADJUSTED_PRODUCT_TYPES.has(adjustedType as AdjustedProductType);
    const totalCompensation = excludesCompensation
      ? (policy.accumulated_savings ?? [])
          .filter((s) => s.deposit_type_code === ContributionType.COMPENSATION)
          .reduce((sum, s) => sum + (s.total_savings || 0), 0)
      : 0;
    const totalPolicyAccumulated = mergedPaths?.reduce((sum, p) => sum + (p.accumulated_amount || 0), 0) || 0;

    return mergedPaths?.map((path, pathIndex) => {
      const fund = fundData?.find(f => f.fund_id === path.path_code);
      const subSpecialization = fund?.sub_specialization || "";
      const isStock = /מניות|סחיר/.test(subSpecialization);

      const liquidPercent = LoanEligibilityConfigService.getMaxLoanPercent(
           product.details.adjusted_product_type?product.details.adjusted_product_type:product.details.product_type,
        true,
        isStock
      );
      const nonLiquidPercent = LoanEligibilityConfigService.getMaxLoanPercent(
           product.details.adjusted_product_type?product.details.adjusted_product_type:product.details.product_type,
        false,
        isStock
      );

      const accumulated = path.accumulated_amount ?? 0;

      // הפחתת החלק היחסי של רכיב הפיצויים מהסכום הזכאי להלוואה, לפי משקל המסלול בסה"כ הצבירה בפוליסה
      const pathCompensation =
        totalCompensation > 0 && totalPolicyAccumulated > 0
          ? totalCompensation * (accumulated / totalPolicyAccumulated)
          : 0;
      const loanEligibleAmount = Math.max(accumulated - pathCompensation, 0);

      // פיצול הסכום הזכאי להלוואה בין נזיל (כבר עבר תאריך נזילות) ללא-נזיל (עתידי/ללא תאריך/לא דווח).
      // מקור אמת יחיד לשני הדברים: אחוז ההלוואה המקסימלי (potential_loan_amount) ותצוגת "סיכום נזילות" - אותו pathLiquidAmount/pathNonLiquidAmount.
      //
      // כשיש מסלול יחיד בפוליסה, כל סכום הנזילות שייך לו במלואו (אין צורך בפיזור פרופורציונלי, שהיה גורם לדילול שגוי).
      // כשיש כמה מסלולים, אין נתון נזילות ברמת המסלול - מפזרים לפי משקל המסלול בסה"כ הצבירה בפוליסה כקירוב.
      const rawPathLiquidAmount =
        alreadyLiquiedAmount <= 0
          ? 0
          : mergedPaths.length === 1
            ? Math.min(alreadyLiquiedAmount, loanEligibleAmount)
            : totalPolicyAccumulated > 0
              ? Math.min(alreadyLiquiedAmount * (accumulated / totalPolicyAccumulated), loanEligibleAmount)
              : 0;
      const rawPathNonLiquidAmount = loanEligibleAmount - rawPathLiquidAmount;

      // טולרנס של 100 ש"ח: פער קטן שכזה נחשב עיגול/דיווח חסר ולא הופך את הסכום ל"מפוצל" - מתייחסים לכל הסכום לפי הצד הדומיננטי
      const pathLiquidAmount = rawPathNonLiquidAmount <= 100 ? loanEligibleAmount : rawPathLiquidAmount;
      const pathNonLiquidAmount = loanEligibleAmount - pathLiquidAmount;

      // "נזיל" אם אין כלל חלק לא-נזיל, "נזיל בעוד X חודשים/שנים" אם יש תאריך נזילות עתידי יחיד ומובחן,
      // אחרת (כמה תאריכים שונים, פער לא-מוסבר, או אין תאריך בכלל) - "לא נזיל" עבור החלק שנותר
      const liquidity_summary = pathNonLiquidAmount <= 0
        ? "נזיל"
        : distinctFutureDatesCount === 1
          ? formatIsoDateYearly(liquidityDate, referencePeriod ?? new Date())
          : "לא נזיל";

      const potential_loan_amount = pathLiquidAmount * liquidPercent + pathNonLiquidAmount * nonLiquidPercent;
      const blendedPercent = loanEligibleAmount > 0 ? potential_loan_amount / loanEligibleAmount : 0;

      return {
        id: `${productIndex}-${policyIndex}-${pathIndex}`,
        adjusted_product_type: product.details.adjusted_product_type,
        product_type: product.details.product_type,
        policy_number: policy.policy_number,
        handler_code: product.details.handler_code,
        path_code: path.path_code, // קוד קרן
        liquidity_summary,
        sub_specialization: subSpecialization,
        avg_annual_yield_trailing_3yrs: fund?.avg_annual_yield_trailing_3yrs,
        avg_annual_yield_trailing_5yrs: fund?.avg_annual_yield_trailing_5yrs,
        accumulated_amount: accumulated || null,
        loan_eligible_amount: pathCompensation > 0 ? loanEligibleAmount : null,
        potential_loan_amount,
        loan_percent: blendedPercent * 100,
        liquid_amount: pathLiquidAmount,
        liquid_percent: liquidPercent * 100,
        non_liquid_amount: pathNonLiquidAmount,
        non_liquid_percent: nonLiquidPercent * 100,
        loans,
        totalLoanAmount,
      };
    }) ?? [];
  })
);;

  // סוגי מוצר שמשויכים לקטגוריית "פנסיה" - השאר משויכים ל"הון"
  const PENSION_ADJUSTED_PRODUCT_TYPES = useMemo(
    () =>
      new Set<number>([
        AdjustedProductType.LIFE_INSURANCE_SAVINGS, // ביטוח מנהלים
        AdjustedProductType.ACTIVE_MANAGERS_INSURANCE, // ביטוח מנהלים
        AdjustedProductType.INACTIVE_MANAGERS_INSURANCE, // ביטוח מנהלים
        AdjustedProductType.PENSION_FUND,
        AdjustedProductType.MANDATORY_PENSION_FUND, // פנסיה מקיפה
        AdjustedProductType.SUPPLEMENTARY_PENSION_FUND, // פנסיה משלימה
      ]),
    []
  );

  // סדר תצוגה מפורש בתוך קטגוריית "הון" - השאר יופיעו אחריהם לפי סדר א"ב
  const CAPITAL_LABEL_ORDER = useMemo(
    () => [
      "פוליסת חיסכון טהור",
      "קרן השתלמות",
      "קופת גמל להשקעה",
      "קופת גמל הונית",
    ],
    []
  );

  // אפשרויות הפילטר לסוג מוצר - הון לפני פנסיה, וסוגים עם אותה תווית (כמו "ביטוח מנהלים") ממוזגים לשורה אחת
  const productTypeOptions = useMemo(() => {
    const typesInTable = new Set<number>();
    allRows.forEach((row) => {
      const type = row.adjusted_product_type ?? row.product_type;
      if (type === null || type === undefined) return;
      typesInTable.add(type);
    });

    const byLabel = new Map<string, { label: string; group: string; values: string[] }>();
    typesInTable.forEach((type) => {
      const label = formatAdjustedProductType(type as AdjustedProductType);
      const group = PENSION_ADJUSTED_PRODUCT_TYPES.has(type) ? "פנסיה" : "הון";
      const groupKey = `${group}::${label}`;
      const existing = byLabel.get(groupKey);
      if (existing) {
        existing.values.push(String(type));
      } else {
        byLabel.set(groupKey, { label, group, values: [String(type)] });
      }
    });

    return Array.from(byLabel.values())
      .map(({ label, group, values }) => ({ value: values[0], label, group, values }))
      .sort((a, b) => {
        if (a.group !== b.group) return a.group === "הון" ? -1 : 1;
        if (a.group === "הון") {
          const aOrder = CAPITAL_LABEL_ORDER.indexOf(a.label);
          const bOrder = CAPITAL_LABEL_ORDER.indexOf(b.label);
          if (aOrder !== -1 || bOrder !== -1) {
            if (aOrder === -1) return 1;
            if (bOrder === -1) return -1;
            return aOrder - bOrder;
          }
        }
        return a.label.localeCompare(b.label);
      });
  }, [allRows, PENSION_ADJUSTED_PRODUCT_TYPES, CAPITAL_LABEL_ORDER]);

  const handlerCodeOptions = useMemo(() => {
    const map = new Map<string, string>();
    allRows.forEach((row) => {
      const code = row.handler_code;
      if (!code) return;
      const key = String(code);
      if (!map.has(key)) map.set(key, formatHandlerCode(code));
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allRows]);

  // פילטר נזילות - שורה עם חלק נזיל וחלק לא נזיל תסונן פנימה תחת שני האפשרויות
  const liquidityOptions = useMemo(
    () => [
      { value: "liquid", label: "נזיל" },
      { value: "non_liquid", label: "לא נזיל" },
    ],
    []
  );

  // דיפולט: הכל בחור, מאותחל ברגע שהאופציות ידועות
  const effectiveSelectedProductTypes =
    selectedProductTypes ?? new Set(productTypeOptions.flatMap((o) => o.values));
  const effectiveSelectedHandlerCodes = selectedHandlerCodes ?? new Set(handlerCodeOptions.map((o) => o.value));
  const effectiveSelectedLiquidityOptions =
    selectedLiquidityOptions ?? new Set(liquidityOptions.map((o) => o.value));

  const rows = useMemo(() => {
    return allRows.filter((row) => {
      const type = row.adjusted_product_type ?? row.product_type;
      const typeKey = type === null || type === undefined ? "" : String(type);
      const handlerKey = row.handler_code ? String(row.handler_code) : "";

      const hasLiquid = (row.liquid_amount ?? 0) > 0;
      const hasNonLiquid = (row.non_liquid_amount ?? 0) > 0;
      const matchesLiquidity =
        (hasLiquid && effectiveSelectedLiquidityOptions.has("liquid")) ||
        (hasNonLiquid && effectiveSelectedLiquidityOptions.has("non_liquid"));

      return (
        effectiveSelectedProductTypes.has(typeKey) &&
        effectiveSelectedHandlerCodes.has(handlerKey) &&
        matchesLiquidity
      );
    });
  }, [allRows, effectiveSelectedProductTypes, effectiveSelectedHandlerCodes, effectiveSelectedLiquidityOptions]);

  // אותו סינון, ברמת המוצרים - לטובת שימוש חוזר בלוגיקת דה-דופליקציה של הלוואות
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const type = product.details.adjusted_product_type ?? product.details.product_type;
      const typeKey = type === null || type === undefined ? "" : String(type);
      const handlerKey = product.details.handler_code ? String(product.details.handler_code) : "";
      return effectiveSelectedProductTypes.has(typeKey) && effectiveSelectedHandlerCodes.has(handlerKey);
    });
  }, [products, effectiveSelectedProductTypes, effectiveSelectedHandlerCodes]);

  // בדיקה אם יש לפחות מוצר אחד עם הלוואות
  const hasAnyLoans = useMemo(() => {
    return rows.some((row) => row.loans && row.loans.length > 0);
  }, [rows]);

  // סיכום סכום, סכום פוטנציאלי להלוואה וסכום הלוואות עבור השורות המסוננות
  const summary = useMemo(() => {
    const accumulated_amount = rows.reduce((sum, row) => sum + (row.accumulated_amount ?? 0), 0);
    const potential_loan_amount = rows.reduce((sum, row) => sum + (row.potential_loan_amount ?? 0), 0);

    // אותה דה-דופליקציה של הלוואות שמשמשת את LoansTable ו-LoanMetricsCards (לפי יצרן + מספר סידורי)
    const totalLoanAmount = MimshakService.getDistinctLoansWithPolicy(filteredProducts).reduce(
      (sum, { loan }) => sum + (loan.loan_amount || 0),
      0
    );

    return { accumulated_amount, potential_loan_amount, totalLoanAmount };
  }, [rows, filteredProducts]);

  // מעקב אחר הלוואות שכבר הוצגו - צריך להיות מחוץ ל-useMemo
  const displayedLoansWithAmount = useMemo(() => new Set<number>(), [rows]);

  const columns: GridColDef[] = useMemo(() => {
    // אתחול מחדש של ה-Set בכל פעם שהעמודות משתנות
    displayedLoansWithAmount.clear();

    const baseColumns: GridColDef[] = [
      { field: "policy_number", headerName: "מספר פוליסה", width: 130 },
      adjustedProductTypeColumn(),
      handlerCodeColumn(),
      {
        field: "accumulated_amount",
        headerName: "סכום",
        width: 140,
        renderCell: (params: any) => {
          if (typeof params.value !== "number" || params.value === 0) return null;
          const eligibleAmount = params.row.loan_eligible_amount;
          return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.3 }}>
              <span dir="ltr" style={{ fontWeight: "bold" }}>
                {formatCurrency(params.value)}
              </span>
              {typeof eligibleAmount === "number" && (
                <Tooltip title="הסכום בגינו תחושב ההלוואה, ללא רכיב פיצויים" arrow>
                  <span dir="ltr" style={{ fontSize: "0.75rem", color: "gray" }}>
                    ({formatCurrency(eligibleAmount)})
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: "loan_percent",
        headerName: "אחוז הלוואה מקסימלי",
        width: 100,
        renderCell: (params: any) => {
          if (typeof params.value !== "number") return params.value ?? "";

          const { liquid_amount, liquid_percent, non_liquid_amount, non_liquid_percent } = params.row;
          const isSplit = liquid_amount > 0 && non_liquid_amount > 0;

          if (!isSplit) {
            return <span dir="ltr">{formatPercentageRound(params.value)}</span>;
          }

          return (
            <Tooltip
              arrow
              title={
                <span>
                  נזיל: {formatCurrency(liquid_amount)} ({formatPercentageRound(liquid_percent)})
                  <br />
                  לא נזיל: {formatCurrency(non_liquid_amount)} ({formatPercentageRound(non_liquid_percent)})
                </span>
              }
            >
              <span dir="ltr">
                {formatPercentageRound(liquid_percent)}, {formatPercentageRound(non_liquid_percent)}
              </span>
            </Tooltip>
          );
        },
      },
      currencyColumn("potential_loan_amount", "סכום פוטנציאלי להלוואה", 180),
    ];

    // הוספת עמודת הלוואות רק אם יש הלוואות - אחרי עמודת הסכום הפוטנציאלי להלוואה
    if (hasAnyLoans) {
      baseColumns.push({
        field: "loans",
        headerName: "הלוואות פעילות",
        width: 200,
        sortComparator: (_v1, _v2, param1, param2) =>
          (param1.api.getRow(param1.id)?.totalLoanAmount ?? 0) - (param2.api.getRow(param2.id)?.totalLoanAmount ?? 0),
        renderCell: (params) => {
          if (!params.row.loans || params.row.loans.length === 0) {
            return null;
          }

          // אם יש יותר מהלוואה אחת, נציג רק את הראשונה עם סכום
          const firstLoan = params.row.loans[0];
          const loanSerialNumber = firstLoan.loan_serial_number;

          // בדיקה אם זה המופע הראשון של ההלוואה
          const isFirstOccurrence = !displayedLoansWithAmount.has(loanSerialNumber);

          if (isFirstOccurrence) {
            displayedLoansWithAmount.add(loanSerialNumber);
          }

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LoanChip policies={[{ loan_details: params.row.loans } as any]} />
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ color: isFirstOccurrence ? "text.primary" : "text.disabled" }}
              >
                {formatCurrency(params.row.totalLoanAmount)}
              </Typography>
            </Box>
          );
        },
      });
    }

    baseColumns.push(
      { field: "liquidity_summary", headerName: "סיכום נזילות", width: 135 },
      { field: "path_code", headerName: "קוד מסלול", width: 100 },
      { field: "sub_specialization", headerName: "תת התמחות", width: 120 },
      percentageColumn("avg_annual_yield_trailing_3yrs", "תשואה ממוצעת לשנה- 3 שנים", 140, false),
      percentageColumn("avg_annual_yield_trailing_5yrs", "תשואה ממוצעת לשנה- 5 שנים", 140, false)
    );

    return baseColumns;
  }, [hasAnyLoans, rows, displayedLoansWithAmount]);

  const handleRowClick = (params: GridRowParams) => {
    const match = products.find((product) =>
      product.policies_or_accounts.some(
        (p) => p.policy_number === params.row.policy_number
      )
    );
    if (match) {
      setSelectedProduct(match);
    }
  };

  const handleCloseDrawer = () => setSelectedProduct(null);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          פוטנציאל להלוואה לפי מוצר ומסלול
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CalculateIcon sx={{ color: "#173829",ml:1.2 }} />}
          onClick={() => {
            setHasOpenedLoanCalculator(true);
            setIsLoanCalculatorOpen(true);
          }}
        >
          חשב הלוואה
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <ExcelStyleFilter
          label="סוג מוצר"
          options={productTypeOptions}
          selected={effectiveSelectedProductTypes}
          onChange={setSelectedProductTypes}
        />
        <ExcelStyleFilter
          label="יצרן"
          options={handlerCodeOptions}
          selected={effectiveSelectedHandlerCodes}
          onChange={setSelectedHandlerCodes}
        />
        <ExcelStyleFilter
          label="נזילות"
          options={liquidityOptions}
          selected={effectiveSelectedLiquidityOptions}
          onChange={setSelectedLiquidityOptions}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 4,
          mb: 1.5,
          p: 1.5,
          borderRadius: 1,
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography variant="body2">
          <strong>סה"כ סכום: </strong>
          <span dir="ltr">{formatCurrency(summary.accumulated_amount)}</span>
        </Typography>
        <Typography variant="body2">
          <strong>סה"כ סכום פוטנציאלי להלוואה: </strong>
          <span dir="ltr">{formatCurrency(summary.potential_loan_amount)}</span>
        </Typography>
        {hasAnyLoans && (
          <Typography variant="body2">
            <strong>סה"כ הלוואות פעילות: </strong>
            <span dir="ltr">{formatCurrency(summary.totalLoanAmount)}</span>
          </Typography>
        )}
      </Box>
      <StyledDataGrid
        rows={rows}
        columns={columns}
        onRowClick={handleRowClick}
        pageSizeOptions={[10, 25, 50]}
        maxHeight="700px"
      />
      {selectedProduct && (
        <InsuranceProductDrawer product={selectedProduct} onClose={handleCloseDrawer} />
      )}

      {hasOpenedLoanCalculator && (
        <Drawer
          anchor="bottom"
          open={isLoanCalculatorOpen}
          onClose={() => setIsLoanCalculatorOpen(false)}
          slotProps={{
            paper: {
              sx: {
                height: "90vh",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              },
            },
          }}
        >
          <Box sx={{ position: "relative", height: "100%" }}>
            <IconButton
              onClick={() => setIsLoanCalculatorOpen(false)}
              sx={{
                position: "absolute",
                left: 16,
                top: 16,
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ height: "100%", overflowY: "auto" }}>
              <LoanCalculator />
            </Box>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default LoanEligibilityTable;
